import { MJCFParser } from "../../core/mjcfParser";
import type { AmbiguousGeomRoleCandidate, MJCFParseResult } from "../../core/mjcfParser";
import { URDFParser } from "../../core/urdfParser";
import {
  applyAmbiguousGeomRoleSelections,
  getAmbiguousGeomRoleCandidatesByWarning,
  type AmbiguousGeomRoleSelections,
} from "../../core/mjcfAmbiguousGeomRoles";
import { loadSceneFromParsedResult } from "../../utils/sceneLoader";
import { syncLibraryGraspsFromSceneRootBodies } from "../../utils/libraryGraspsSync";
import { applyPhysicsTimestepFromXmlFile } from "../../utils/mjcfOptions";
import { indexUploadedMeshFiles } from "../../mesh-editor/uploadIndex";
import { showNoRobotVisualsNoticeIfNeeded } from "../../utils/robotVisualNotice";
import { logger } from "../../utils/logger";
import { expandXacroFile } from "../../utils/xacroExpansion";
import { addBreadcrumb, captureError } from "../../monitoring/sentry";
import { getSceneObservabilitySummary } from "../../monitoring/sceneObservability";
import { getSceneActions, getSceneState } from "../sceneStore";
import { getHistoryActions } from "../historyStore";
import { getAssetEntries } from "../assetStore";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { buildProjectSaveSnapshotHash } from "../../utils/projectSaveSnapshot";
import { getProjectSaveActions } from "../projectSaveStore";
import {
  collectXmlUploadDependenciesFromFileTree,
  expandMjcfIncludesToFile,
  formatMjcfMissingDependencies,
  getMissingMjcfUploadDependencies,
  getRegisteredMjcfUploadDependencies,
  getXmlDocumentKind,
} from "../../utils/mjcfUploadDependencies";
import type { MjcfUploadDependency } from "../../utils/mjcfUploadDependencies";
import type {
  FileInputChangeEvent,
  FileMap,
  FileStoreActions,
  FileStoreGet,
  FileStoreSet,
  PendingImportWarningOptions,
} from "./types";

let pendingMjcfImportContinuation:
  | ((options?: PendingImportWarningOptions) => Promise<void>)
  | null = null;
let pendingMjcfImportCancelContinuation: (() => void) | null = null;
let pendingRegisteredAssetImportContinuation: (() => Promise<void>) | null = null;
let pendingSkipMissingAssetImportContinuation: (() => Promise<void>) | null = null;
let pendingAmbiguousGeomRoleCandidates: AmbiguousGeomRoleCandidate[] = [];
const REGISTERED_ASSET_LABEL_SUFFIX = " (detected in asset store)";
const UPLOAD_ISSUE_FLOW_PLAN_EVENT = "critter-upload-issue-flow-plan";

const getSafeFileExtension = (fileName: string | undefined | null): string => {
  const match = String(fileName || "")
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/);
  return match?.[1] || "unknown";
};

const isImportableXmlFileName = (fileName: string | undefined | null): boolean => {
  const lower = String(fileName || "").toLowerCase();
  return lower.endsWith(".xml") || lower.endsWith(".urdf") || lower.endsWith(".xacro");
};

const isXacroFileName = (fileName: string | undefined | null): boolean =>
  String(fileName || "")
    .toLowerCase()
    .endsWith(".xacro");

const parseXmlDocumentKindFromFile = async (
  xmlFile: File
): Promise<"mjcf" | "urdf" | "unknown"> => {
  const text = await xmlFile.text();
  const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError) throw new Error(`XML parsing failed for ${xmlFile.name}`);
  return getXmlDocumentKind(xmlDoc);
};

const parseXmlFileForImport = async (
  xmlFile: File,
  fileMap: FileMap
): Promise<{ parsedResult: MJCFParseResult; importXmlFile: File; kind: "mjcf" | "urdf" }> => {
  if (isXacroFileName(xmlFile.name)) {
    const expanded = await expandXacroFile(xmlFile, fileMap);
    const parser = new URDFParser();
    const parsedResult = (await parser.parseFile(expanded.urdfFile)) as MJCFParseResult;
    parsedResult.parseWarnings = [...expanded.warnings, ...normalizeParseWarnings(parsedResult)];
    parsedResult.referencedFiles = Array.from(
      new Set([...(parsedResult.referencedFiles || []), ...expanded.referencedFiles])
    );
    return {
      parsedResult,
      importXmlFile: expanded.urdfFile,
      kind: "urdf",
    };
  }
  const extension = getSafeFileExtension(xmlFile.name);
  const kind = extension === "urdf" ? "urdf" : await parseXmlDocumentKindFromFile(xmlFile);
  if (kind === "urdf") {
    const parser = new URDFParser();
    return {
      parsedResult: (await parser.parseFile(xmlFile)) as MJCFParseResult,
      importXmlFile: xmlFile,
      kind,
    };
  }
  if (kind === "mjcf") {
    const importXmlFile = await expandMjcfIncludesToFile(xmlFile, fileMap);
    const parser = new MJCFParser();
    return {
      parsedResult: (await parser.parseFile(importXmlFile)) as MJCFParseResult,
      importXmlFile,
      kind,
    };
  }
  throw new Error("XML import expected a <mujoco> or <robot> root element.");
};

const normalizeParseWarnings = (parsedResult: MJCFParseResult): string[] => {
  const warnings = parsedResult.parseWarnings;
  if (!Array.isArray(warnings)) return [];
  return warnings.map((warning) => String(warning || "").trim()).filter(Boolean);
};

const dispatchUploadIssueFlowPlan = (issueTypes: string[]): void => {
  if (typeof window === "undefined") return;
  const uniqueIssueTypes = Array.from(
    new Set(issueTypes.map((issueType) => String(issueType || "").trim()).filter(Boolean))
  );
  if (uniqueIssueTypes.length === 0) return;
  window.dispatchEvent(
    new CustomEvent(UPLOAD_ISSUE_FLOW_PLAN_EVENT, {
      detail: { issueTypes: uniqueIssueTypes },
    })
  );
};

const planXmlUploadIssues = async (
  xmlFile: File,
  fileMap: FileMap,
  missingResolution: { missingFiles: string[] }
): Promise<void> => {
  const issueTypes: string[] = [];
  if (missingResolution.missingFiles.length > 0) issueTypes.push("missingDependencies");

  try {
    const { parsedResult } = await parseXmlFileForImport(xmlFile, fileMap);
    if (normalizeParseWarnings(parsedResult).length > 0) issueTypes.push("mjcfWarnings");
  } catch (error) {
    logger.debug("[fileStore] Could not preflight all XML upload issues.", {
      fileName: xmlFile.name,
      error,
    });
  }

  dispatchUploadIssueFlowPlan(issueTypes);
};

type UploadFile = File & {
  webkitRelativePath?: string;
  __critterPreferredImportRoot?: boolean;
};

type AssetValidationSampleEntry = {
  requestedPath?: unknown;
  meshUuid?: unknown;
};

type MeshAssetDef = {
  url?: unknown;
  name?: unknown;
};

type TextureAssetDef = {
  file?: unknown;
};

type HFieldAssetDef = {
  file?: unknown;
  name?: unknown;
};

type ParsedGeomAssetRef = {
  mesh?: unknown;
  kind?: unknown;
  heightField?: unknown;
};

type ParsedBodyAssetRefs = {
  geoms?: ParsedGeomAssetRef[];
};

type ParsedMaterialTextureRef = {
  colorMap?: unknown;
  colorMapRepeat?: unknown;
  colorMapOffset?: unknown;
  colorMapCenter?: unknown;
  colorMapRotation?: unknown;
  colorMapWrap?: unknown;
};

const dependencyKey = (dependency: MjcfUploadDependency): string =>
  `${dependency.kind}:${dependency.path.toLowerCase()}`;

const canSkipMissingDependency = (dependency: MjcfUploadDependency): boolean =>
  dependency.kind === "mesh" ||
  dependency.kind === "texture" ||
  dependency.kind === "hfield" ||
  (dependency.kind === "include" && dependency.source === "xacro");

const markRegisteredDependencyLabels = (
  missingDependencies: MjcfUploadDependency[],
  registeredDependencies: MjcfUploadDependency[]
): string[] => {
  const registeredKeys = new Set(registeredDependencies.map(dependencyKey));
  return missingDependencies.map((dependency) => {
    const [label] = formatMjcfMissingDependencies([dependency]);
    return registeredKeys.has(dependencyKey(dependency))
      ? `${label}${REGISTERED_ASSET_LABEL_SUFFIX}`
      : label;
  });
};

const findUploadDependencyResolution = async (
  xmlFile: File,
  fileMap: FileMap
): Promise<{
  missingFiles: string[];
  missingDependencies: MjcfUploadDependency[];
  canProceedWithRegisteredAssets: boolean;
  canProceedWithoutMissingAssets: boolean;
}> => {
  const dependencies = await collectXmlUploadDependenciesFromFileTree(xmlFile, fileMap);
  const registeredAssets = Object.values(getAssetEntries() || {});
  const missingFromUpload = getMissingMjcfUploadDependencies(dependencies, fileMap, []);
  const registeredAvailable = getRegisteredMjcfUploadDependencies(
    dependencies,
    fileMap,
    registeredAssets
  );
  const registeredKeys = new Set(registeredAvailable.map(dependencyKey));
  return {
    missingFiles: markRegisteredDependencyLabels(missingFromUpload, registeredAvailable),
    missingDependencies: missingFromUpload,
    canProceedWithRegisteredAssets:
      missingFromUpload.length > 0 &&
      missingFromUpload.every(
        (dependency) => dependency.kind === "mesh" && registeredKeys.has(dependencyKey(dependency))
      ),
    canProceedWithoutMissingAssets:
      missingFromUpload.length > 0 &&
      missingFromUpload.every((dependency) => canSkipMissingDependency(dependency)),
  };
};

const normalizeDependencyPath = (value: string | undefined | null): string =>
  String(value || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "")
    .toLowerCase();

const basenameOfDependencyPath = (value: string | undefined | null): string => {
  const normalized = normalizeDependencyPath(value);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

const dependencyPathMatches = (candidate: string | undefined, requested: string): boolean => {
  const normalizedCandidate = normalizeDependencyPath(candidate);
  const normalizedRequested = normalizeDependencyPath(requested);
  if (!normalizedCandidate || !normalizedRequested) return false;
  if (normalizedCandidate === normalizedRequested) return true;
  if (
    normalizedRequested.startsWith("assets/") &&
    normalizedCandidate === normalizedRequested.replace(/^assets\//, "")
  ) {
    return true;
  }
  if (
    !normalizedRequested.startsWith("assets/") &&
    normalizedCandidate === `assets/${normalizedRequested}`
  ) {
    return true;
  }
  return (
    basenameOfDependencyPath(normalizedCandidate) === basenameOfDependencyPath(normalizedRequested)
  );
};

const cloneRecord = <T>(record: Record<string, T> | undefined): Record<string, T> | undefined => {
  if (!record) return record;
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      value && typeof value === "object" ? ({ ...value } as T) : value,
    ])
  ) as Record<string, T>;
};

const pruneParsedResultMissingAssets = (
  parsedResult: MJCFParseResult,
  missingDependencies: MjcfUploadDependency[]
): MJCFParseResult => {
  const missingMeshes = missingDependencies.filter((dependency) => dependency.kind === "mesh");
  const missingTextures = missingDependencies.filter((dependency) => dependency.kind === "texture");
  const missingHFields = missingDependencies.filter((dependency) => dependency.kind === "hfield");
  if (missingMeshes.length === 0 && missingTextures.length === 0 && missingHFields.length === 0) {
    return parsedResult;
  }

  const meshes = cloneRecord(parsedResult.meshes) || {};
  const meshIdsToRemove = new Set<string>();
  Object.entries(meshes).forEach(([meshId, meshDef]) => {
    const mesh = meshDef as MeshAssetDef;
    const meshUrl = typeof mesh.url === "string" ? mesh.url : "";
    const meshName = typeof mesh.name === "string" ? mesh.name : "";
    if (
      missingMeshes.some(
        (dependency) =>
          dependencyPathMatches(meshUrl, dependency.path) ||
          dependencyPathMatches(meshName, dependency.path)
      )
    ) {
      meshIdsToRemove.add(meshId);
    }
  });
  meshIdsToRemove.forEach((meshId) => delete meshes[meshId]);

  const textures = cloneRecord(parsedResult.textures) || {};
  const textureIdsToRemove = new Set<string>();
  Object.entries(textures).forEach(([textureId, textureDef]) => {
    const texture = textureDef as TextureAssetDef;
    const textureFile = typeof texture.file === "string" ? texture.file : "";
    if (missingTextures.some((dependency) => dependencyPathMatches(textureFile, dependency.path))) {
      textureIdsToRemove.add(textureId);
    }
  });
  textureIdsToRemove.forEach((textureId) => delete textures[textureId]);

  const hfields = cloneRecord(parsedResult.hfields) || {};
  const hfieldIdsToRemove = new Set<string>();
  Object.entries(hfields).forEach(([hfieldId, hfieldDef]) => {
    const hfield = hfieldDef as HFieldAssetDef;
    const hfieldFile = typeof hfield.file === "string" ? hfield.file : "";
    const hfieldName = typeof hfield.name === "string" ? hfield.name : "";
    if (
      missingHFields.some(
        (dependency) =>
          dependencyPathMatches(hfieldFile, dependency.path) ||
          dependencyPathMatches(hfieldName, dependency.path)
      )
    ) {
      hfieldIdsToRemove.add(hfieldId);
    }
  });
  hfieldIdsToRemove.forEach((hfieldId) => delete hfields[hfieldId]);

  const bodies = cloneRecord(parsedResult.bodies) || {};
  Object.entries(bodies).forEach(([bodyId, body]) => {
    const bodyWithAssets = body as ParsedBodyAssetRefs;
    const originalGeoms = bodyWithAssets.geoms;
    if (!body || !Array.isArray(originalGeoms)) return;
    const geoms = originalGeoms.filter((geom) => {
      if (!geom) return true;
      if (typeof geom.mesh === "string" && meshIdsToRemove.has(geom.mesh)) return false;
      if (
        geom.kind === "hfield" &&
        typeof geom.heightField === "string" &&
        hfieldIdsToRemove.has(geom.heightField)
      ) {
        return false;
      }
      return true;
    });
    bodies[bodyId] = geoms.length === originalGeoms.length ? body : { ...body, geoms };
  });

  const materials = cloneRecord(parsedResult.materials) || {};
  Object.entries(materials).forEach(([materialId, material]) => {
    const materialRef = material as ParsedMaterialTextureRef;
    if (!material || typeof materialRef.colorMap !== "string") return;
    if (!textureIdsToRemove.has(materialRef.colorMap)) return;
    const next = { ...materialRef };
    delete next.colorMap;
    delete next.colorMapRepeat;
    delete next.colorMapOffset;
    delete next.colorMapCenter;
    delete next.colorMapRotation;
    delete next.colorMapWrap;
    materials[materialId] = next as (typeof materials)[string];
  });

  return {
    ...parsedResult,
    bodies,
    meshes,
    textures,
    hfields,
    materials,
    referencedFiles: (parsedResult.referencedFiles || []).filter((filename) => {
      const dependency = String(filename || "");
      return !missingDependencies.some((missing) =>
        dependencyPathMatches(dependency, missing.path)
      );
    }),
  } as MJCFParseResult;
};

const missingFilesFromAssetValidationError = (error: unknown): string[] => {
  const details = (error as { details?: { sample?: unknown[] } } | null)?.details;
  if (!details || !Array.isArray(details.sample)) return [];
  return details.sample
    .map((rawEntry) => {
      const entry = rawEntry as AssetValidationSampleEntry;
      const requestedPath =
        typeof entry?.requestedPath === "string" ? entry.requestedPath.trim() : "";
      const meshUuid = typeof entry?.meshUuid === "string" ? entry.meshUuid.trim() : "";
      const label = requestedPath || meshUuid;
      return label ? `mesh: ${label}` : "";
    })
    .filter(Boolean);
};

const showUploadWarning = (message: string): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("critter-toast", {
      detail: { message },
    })
  );
};

export const createXmlActions = ({
  set,
  get,
  applySceneConfigFromFileMap,
}: {
  set: FileStoreSet;
  get: FileStoreGet;
  applySceneConfigFromFileMap: (fileMap: FileMap) => Promise<void>;
}): Pick<
  FileStoreActions,
  | "handleFiles"
  | "parseXmlFile"
  | "proceedWithRegisteredMissingAssets"
  | "proceedWithoutMissingAssets"
  | "cancelMissingUploadDependencies"
  | "continuePendingMjcfImportWithWarnings"
  | "cancelPendingMjcfImportWithWarnings"
  | "promptPendingMjcfImportWarnings"
> => ({
  promptPendingMjcfImportWarnings: (args: {
    fileName: string;
    warnings: string[];
    onContinue: (options?: PendingImportWarningOptions) => Promise<void>;
    onCancel?: () => void;
  }): void => {
    pendingMjcfImportContinuation = args.onContinue;
    pendingMjcfImportCancelContinuation = args.onCancel || null;
    pendingAmbiguousGeomRoleCandidates = [];
    set({
      pendingMjcfImportWarnings: {
        fileName: args.fileName || "MJCF import",
        warnings: Array.isArray(args.warnings)
          ? args.warnings.map((warning) => String(warning || "").trim()).filter(Boolean)
          : [],
      },
    });
  },

  proceedWithRegisteredMissingAssets: async (): Promise<void> => {
    const continuation = pendingRegisteredAssetImportContinuation;
    pendingRegisteredAssetImportContinuation = null;
    pendingSkipMissingAssetImportContinuation = null;
    set({ missingFiles: [], referencedFiles: [], xmlFile: null });
    if (!continuation) return;
    try {
      set({ uploadingFiles: true });
      await continuation();
    } finally {
      set({ uploadingFiles: false });
    }
  },

  proceedWithoutMissingAssets: async (): Promise<void> => {
    const continuation = pendingSkipMissingAssetImportContinuation;
    pendingRegisteredAssetImportContinuation = null;
    pendingSkipMissingAssetImportContinuation = null;
    set({ missingFiles: [], referencedFiles: [], xmlFile: null });
    if (!continuation) return;
    try {
      set({ uploadingFiles: true });
      await continuation();
    } finally {
      set({ uploadingFiles: false });
    }
  },

  cancelMissingUploadDependencies: (): void => {
    pendingRegisteredAssetImportContinuation = null;
    pendingSkipMissingAssetImportContinuation = null;
    pendingAmbiguousGeomRoleCandidates = [];
    set({
      missingFiles: [],
      referencedFiles: [],
      xmlFile: null,
    });
  },

  handleFiles: async (event: FileInputChangeEvent): Promise<void> => {
    if (shouldBlockSceneAuthoringMutation("handleFiles")) {
      event.target.value = "";
      return;
    }
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) {
      return;
    }
    // Lightweight pre-indexing only (no geometry-heavy analysis on upload).
    indexUploadedMeshFiles(files);
    let xmlFileForMissingPrompt: File | null = null;

    try {
      set({ uploadingFiles: true });

      const currentState = get();
      const currentFileMap = currentState.fileMap;
      const newFileMap: FileMap = { ...currentFileMap };
      const uploadedFileMap: FileMap = {};
      let hasXmlFile = false;
      let firstXml: File | null = null;
      let modelXml: File | null = null;
      let preferredXml: File | null = null;

      for (const file of files) {
        const relativePath =
          typeof (file as UploadFile).webkitRelativePath === "string"
            ? (file as UploadFile).webkitRelativePath.replace(/\\/g, "/")
            : "";
        if (relativePath) {
          newFileMap[relativePath] = file;
          uploadedFileMap[relativePath] = file;
        }
        if (!newFileMap[file.name]) newFileMap[file.name] = file;
        if (!uploadedFileMap[file.name]) uploadedFileMap[file.name] = file;
        const lower = file.name.toLowerCase();
        if (!isImportableXmlFileName(lower)) continue;
        if (!firstXml) firstXml = file;
        if ((file as UploadFile).__critterPreferredImportRoot) preferredXml = file;
        if (
          lower === "model.xml" ||
          lower === "model.urdf" ||
          lower === "model.xacro" ||
          lower === "model.urdf.xacro"
        )
          modelXml = file;
      }

      set({ fileMap: newFileMap });

      const nextXml = preferredXml || modelXml || firstXml;
      if (nextXml) {
        hasXmlFile = true;
      }

      // When resolving missing dependencies for an existing XML file, keep that file as the
      // root to load. Newly uploaded XMLs in this flow are includes/dependencies, not new roots.
      const isResolvingMissingFiles =
        currentState.missingFiles.length > 0 && !!currentState.xmlFile;
      const xmlFile = isResolvingMissingFiles
        ? currentState.xmlFile
        : nextXml || currentState.xmlFile;
      xmlFileForMissingPrompt = xmlFile;
      const shouldLoadXml = !!xmlFile && (hasXmlFile || currentState.missingFiles.length > 0);
      if (shouldLoadXml) {
        addBreadcrumb("mjcf_upload_started", {
          area: "mjcf-import",
          action: "handleFiles",
          operation: "mjcf-upload",
          phase: "upload-started",
          fileExtension: getSafeFileExtension(xmlFile?.name),
          uploadedFileCount: files.length,
          fileMapEntryCount: Object.keys(newFileMap || {}).length,
        });
      }
      const importResolvedXml = async (skipMissingDependencies: MjcfUploadDependency[] = []) => {
        if (!xmlFile || !shouldLoadXml) return;
        const sceneActions = getSceneActions();

        const { parsedResult, importXmlFile, kind } = await parseXmlFileForImport(
          xmlFile,
          newFileMap
        );
        const sceneParsedResult = pruneParsedResultMissingAssets(
          parsedResult,
          skipMissingDependencies
        );
        if (kind === "mjcf")
          await applyPhysicsTimestepFromXmlFile(importXmlFile, importXmlFile.name);
        const continueImport = async (roleOptions?: {
          ambiguousGeomRoles?: AmbiguousGeomRoleSelections;
        }) => {
          applyAmbiguousGeomRoleSelections(
            sceneParsedResult,
            pendingAmbiguousGeomRoleCandidates,
            roleOptions?.ambiguousGeomRoles
          );
          set({
            pendingMjcfImportWarnings: null,
            referencedFiles: sceneParsedResult.referencedFiles,
            missingFiles: [],
            keyframes: sceneParsedResult.keyframes,
            keyframeDetails: sceneParsedResult.keyframeDetails || {},
            preservedMjcfSections: sceneParsedResult.preservedMjcfSections || {},
          });

          await loadSceneFromParsedResult(sceneParsedResult, sceneActions, {
            resetScene: true,
            mergeWithExisting: false,
            processAssets: true,
            strictAssetValidation: true,
            saveToHistory: true,
            fileMap: newFileMap,
          });

          await applySceneConfigFromFileMap(newFileMap);
          await syncLibraryGraspsFromSceneRootBodies();
          showNoRobotVisualsNoticeIfNeeded(sceneParsedResult);
          set({ xmlFile: null });
          addBreadcrumb("mjcf_scene_loaded", {
            area: "mjcf-import",
            action: "handleFiles",
            operation: "mjcf-upload",
            phase: "scene-loaded",
            fileExtension: getSafeFileExtension(xmlFile.name),
            uploadedFileCount: files.length,
            fileMapEntryCount: Object.keys(newFileMap || {}).length,
            missingAssetCount: 0,
            ...getSceneObservabilitySummary(getSceneState()),
          });
          getProjectSaveActions().markProjectSaved(
            buildProjectSaveSnapshotHash({
              scene: getSceneState(),
              fileState: get(),
            }),
            "import"
          );
          getHistoryActions().clearHistory();
        };
        const parseWarnings = normalizeParseWarnings(parsedResult);
        if (parseWarnings.length > 0) {
          pendingAmbiguousGeomRoleCandidates = getAmbiguousGeomRoleCandidatesByWarning(
            parsedResult,
            parseWarnings
          );
          pendingMjcfImportContinuation = continueImport;
          set({
            pendingMjcfImportWarnings: {
              fileName:
                importXmlFile.name ||
                xmlFile.name ||
                (kind === "urdf" ? "URDF import" : "MJCF import"),
              warnings: parseWarnings,
            },
          });
          addBreadcrumb(
            "mjcf_parse_warnings_pending",
            {
              area: "mjcf-import",
              action: "handleFiles",
              operation: "mjcf-upload",
              phase: "warnings-pending",
              fileExtension: getSafeFileExtension(xmlFile.name),
              uploadedFileCount: files.length,
            },
            "warning"
          );
          return;
        }
        await continueImport();
      };
      if (xmlFile) {
        const missingResolution = await findUploadDependencyResolution(xmlFile, newFileMap);
        if (missingResolution.missingFiles.length > 0) {
          await planXmlUploadIssues(xmlFile, newFileMap, missingResolution);
          pendingRegisteredAssetImportContinuation =
            missingResolution.canProceedWithRegisteredAssets
              ? async () => {
                  set({ missingFiles: [], referencedFiles: [], xmlFile: null });
                  await importResolvedXml();
                }
              : null;
          pendingSkipMissingAssetImportContinuation =
            missingResolution.canProceedWithoutMissingAssets
              ? async () => {
                  set({ missingFiles: [], referencedFiles: [], xmlFile: null });
                  await importResolvedXml(missingResolution.missingDependencies);
                }
              : null;
          set({
            xmlFile,
            fileMap: newFileMap,
            referencedFiles: missingResolution.missingFiles,
            missingFiles: missingResolution.missingFiles,
          });
          addBreadcrumb(
            "mjcf_missing_assets_pending",
            {
              area: "mjcf-import",
              action: "handleFiles",
              operation: "mjcf-upload",
              phase: "missing-assets-pending",
              fileExtension: getSafeFileExtension(xmlFile.name),
              uploadedFileCount: files.length,
              missingAssetCount: missingResolution.missingFiles.length,
            },
            "warning"
          );
          return;
        }
        // Clear xmlFile from the store atomically with missingFiles so the App-level
        // xmlFile subscriber sees xmlFile=null and does not call parseXmlFile concurrently.
        // handleFiles uses its local `xmlFile` variable for the load below.
        set({ missingFiles: [], xmlFile: null });
      }

      if (shouldLoadXml) {
        await importResolvedXml();
      } else if (!xmlFile && Object.keys(newFileMap).length > 0) {
        setTimeout(async () => {
          try {
            const sceneActions = getSceneActions();

            const meshLoader = await import("../../utils/meshLoader");
            const result = await meshLoader.processUploadedMeshes(
              uploadedFileMap,
              (processing: boolean) => set({ processingMeshes: processing }),
              sceneActions,
              { getSceneState }
            );

            if (!result?.success) {
              logger.warn("[fileStore] Mesh processing failed:", result?.message);
              showUploadWarning(
                result?.message ? `Mesh import failed: ${result.message}.` : "Mesh import failed."
              );
            }
          } catch (meshError) {
            logger.error("[fileStore] Failed to load meshes without XML:", meshError);
            showUploadWarning("Mesh import failed. Check the console for details.");
          }
        }, 0);
      }
    } catch (error) {
      const missingFromValidation = missingFilesFromAssetValidationError(error);
      if (missingFromValidation.length > 0) {
        set({
          referencedFiles: missingFromValidation,
          missingFiles: missingFromValidation,
          ...(xmlFileForMissingPrompt ? { xmlFile: xmlFileForMissingPrompt } : {}),
        });
        return;
      }
      logger.error("Error handling files:", error);
      addBreadcrumb(
        "mjcf_upload_failed",
        {
          area: "mjcf-import",
          action: "handleFiles",
          operation: "mjcf-upload",
          phase: "failed",
          fileExtension: getSafeFileExtension(xmlFileForMissingPrompt?.name),
          uploadedFileCount: files.length,
        },
        "error"
      );
      captureError(error, {
        area: "mjcf-import",
        action: "handleFiles",
        fileName: xmlFileForMissingPrompt?.name || null,
        uploadedFileCount: files.length,
      });
      showUploadWarning("File import failed. Check the console for details.");
    } finally {
      set({ uploadingFiles: false });
      event.target.value = "";
    }
  },

  parseXmlFile: async (
    xmlFile: File,
    fileMap: FileMap,
    options?: { worldBodyNameBase?: string; meshLibraryPathPrefix?: string }
  ): Promise<void> => {
    if (!xmlFile) return;

    try {
      set({ parsingXml: true });
      addBreadcrumb("mjcf_parse_started", {
        area: "mjcf-import",
        action: "parseXmlFile",
        operation: "mjcf-upload",
        phase: "parse-started",
        fileExtension: getSafeFileExtension(xmlFile.name),
        fileMapEntryCount: Object.keys(fileMap || {}).length,
      });

      const importResolvedXml = async (skipMissingDependencies: MjcfUploadDependency[] = []) => {
        const { parsedResult, importXmlFile, kind } = await parseXmlFileForImport(xmlFile, fileMap);
        const sceneParsedResult = pruneParsedResultMissingAssets(
          parsedResult,
          skipMissingDependencies
        );
        if (kind === "mjcf")
          await applyPhysicsTimestepFromXmlFile(importXmlFile, importXmlFile.name);
        const { referencedFiles: parsedReferencedFiles, keyframes: parsedKeyframes } =
          sceneParsedResult;
        const continueImport = async (roleOptions?: {
          ambiguousGeomRoles?: AmbiguousGeomRoleSelections;
        }) => {
          applyAmbiguousGeomRoleSelections(
            sceneParsedResult,
            pendingAmbiguousGeomRoleCandidates,
            roleOptions?.ambiguousGeomRoles
          );
          await loadSceneFromParsedResult(sceneParsedResult, getSceneActions(), {
            resetScene: true,
            mergeWithExisting: false,
            processAssets: true,
            strictAssetValidation: true,
            saveToHistory: true,
            worldBodyNameBase: options?.worldBodyNameBase,
            meshLibraryPathPrefix: options?.meshLibraryPathPrefix,
            fileMap,
          });

          set({
            pendingMjcfImportWarnings: null,
            referencedFiles: parsedReferencedFiles,
            keyframes: parsedKeyframes,
            keyframeDetails: sceneParsedResult.keyframeDetails || {},
            preservedMjcfSections: sceneParsedResult.preservedMjcfSections || {},
          });

          await applySceneConfigFromFileMap(fileMap);
          await syncLibraryGraspsFromSceneRootBodies();
          showNoRobotVisualsNoticeIfNeeded(sceneParsedResult);

          const missing = parsedReferencedFiles.filter((filename) => {
            if (fileMap[filename]) return false;
            if (fileMap[`assets/${filename}`]) return false;
            if (filename.startsWith("assets/") && fileMap[filename.replace("assets/", "")])
              return false;
            const dependency = filename
              .toLowerCase()
              .match(/\.(png|jpg|jpeg|webp|bmp|tga|ktx|ktx2)$/)
              ? ({ kind: "texture", path: filename } as const)
              : ({ kind: "mesh", path: filename } as const);
            if (
              getMissingMjcfUploadDependencies(
                [dependency],
                fileMap,
                Object.values(getAssetEntries() || {})
              ).length === 0
            ) {
              return false;
            }
            return true;
          });

          set({ missingFiles: missing, xmlFile: null });
          addBreadcrumb("mjcf_scene_loaded", {
            area: "mjcf-import",
            action: "parseXmlFile",
            operation: "mjcf-upload",
            phase: "scene-loaded",
            fileExtension: getSafeFileExtension(xmlFile.name),
            fileMapEntryCount: Object.keys(fileMap || {}).length,
            missingAssetCount: missing.length,
            ...getSceneObservabilitySummary(getSceneState()),
          });
          getProjectSaveActions().markProjectSaved(
            buildProjectSaveSnapshotHash({
              scene: getSceneState(),
              fileState: get(),
            }),
            "import"
          );
          getHistoryActions().clearHistory();
        };
        const parseWarnings = normalizeParseWarnings(parsedResult);
        if (parseWarnings.length > 0) {
          pendingAmbiguousGeomRoleCandidates = getAmbiguousGeomRoleCandidatesByWarning(
            parsedResult,
            parseWarnings
          );
          pendingMjcfImportContinuation = continueImport;
          set({
            pendingMjcfImportWarnings: {
              fileName:
                importXmlFile.name ||
                xmlFile.name ||
                (kind === "urdf" ? "URDF import" : "MJCF import"),
              warnings: parseWarnings,
            },
          });
          addBreadcrumb(
            "mjcf_parse_warnings_pending",
            {
              area: "mjcf-import",
              action: "parseXmlFile",
              operation: "mjcf-upload",
              phase: "warnings-pending",
              fileExtension: getSafeFileExtension(xmlFile.name),
            },
            "warning"
          );
          return;
        }
        await continueImport();
      };

      const missingResolution = await findUploadDependencyResolution(xmlFile, fileMap);
      if (missingResolution.missingFiles.length > 0) {
        await planXmlUploadIssues(xmlFile, fileMap, missingResolution);
        pendingRegisteredAssetImportContinuation = missingResolution.canProceedWithRegisteredAssets
          ? async () => {
              set({ missingFiles: [], referencedFiles: [], xmlFile: null });
              await importResolvedXml();
            }
          : null;
        pendingSkipMissingAssetImportContinuation = missingResolution.canProceedWithoutMissingAssets
          ? async () => {
              set({ missingFiles: [], referencedFiles: [], xmlFile: null });
              await importResolvedXml(missingResolution.missingDependencies);
            }
          : null;
        set({
          referencedFiles: missingResolution.missingFiles,
          missingFiles: missingResolution.missingFiles,
          xmlFile,
        });
        addBreadcrumb(
          "mjcf_missing_assets_pending",
          {
            area: "mjcf-import",
            action: "parseXmlFile",
            operation: "mjcf-upload",
            phase: "missing-assets-pending",
            fileExtension: getSafeFileExtension(xmlFile.name),
            missingAssetCount: missingResolution.missingFiles.length,
          },
          "warning"
        );
        return;
      }
      await importResolvedXml();
    } catch (error) {
      const missingFromValidation = missingFilesFromAssetValidationError(error);
      if (missingFromValidation.length > 0) {
        set({
          referencedFiles: missingFromValidation,
          missingFiles: missingFromValidation,
          xmlFile,
        });
        return;
      }
      logger.error("Failed to parse XML file:", error);
      addBreadcrumb(
        "mjcf_parse_failed",
        {
          area: "mjcf-import",
          action: "parseXmlFile",
          operation: "mjcf-upload",
          phase: "failed",
          fileExtension: getSafeFileExtension(xmlFile.name),
          fileMapEntryCount: Object.keys(fileMap || {}).length,
        },
        "error"
      );
      captureError(error, {
        area: "mjcf-import",
        action: "parseXmlFile",
        fileName: xmlFile.name || null,
        fileMapEntryCount: Object.keys(fileMap || {}).length,
      });
      showUploadWarning("XML import failed. Check the console for details.");
    } finally {
      set({ parsingXml: false });
    }
  },

  continuePendingMjcfImportWithWarnings: async (
    options?: PendingImportWarningOptions
  ): Promise<void> => {
    const continuation = pendingMjcfImportContinuation;
    pendingMjcfImportContinuation = null;
    pendingMjcfImportCancelContinuation = null;
    if (!continuation) {
      pendingAmbiguousGeomRoleCandidates = [];
      set({ pendingMjcfImportWarnings: null });
      return;
    }
    try {
      await continuation(options);
    } finally {
      pendingAmbiguousGeomRoleCandidates = [];
      set({ pendingMjcfImportWarnings: null });
    }
  },

  cancelPendingMjcfImportWithWarnings: (): void => {
    const cancelContinuation = pendingMjcfImportCancelContinuation;
    pendingMjcfImportContinuation = null;
    pendingMjcfImportCancelContinuation = null;
    pendingRegisteredAssetImportContinuation = null;
    pendingSkipMissingAssetImportContinuation = null;
    pendingAmbiguousGeomRoleCandidates = [];
    set({ pendingMjcfImportWarnings: null, xmlFile: null });
    if (cancelContinuation) cancelContinuation();
  },
});
